import Markdown from '@/components/dataDisplay/Markdown';
import { Agency } from '@/modules/core/data/types/Agency';
import { Asset } from '@/modules/core/data/types/Asset';
import { Contact } from '@/modules/core/data/types/Contact';
import { LabeledLink } from '@/modules/core/data/types/LabeledLink';
import { Logo } from '@/modules/core/data/types/Logo';
import { Office } from '@/modules/core/data/types/Office';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { isMobileDevice } from '@/utils/mobile';
import { TFunction } from 'i18next';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import map from 'lodash.map';
import size from 'lodash.size';
import React from 'react';
import { renderToString } from 'react-dom/server';
import { v1 as uuid } from 'uuid';
import { hasValue } from '../core/data/record';
import { groupContactsByOffice } from '../solutions/contact';

// TODO Copied/pasted from TFP v2, should be completely wiped and done properly using proper react templates!

// TODO constants
const classAgency = 'borrowing-assistance-agency';
const classContactAgency = 'borrowing-assistance-agency-contact';

export const generateContacts = (
  contacts: Contact[],
  {
    before = '',
    beforeEach = '',
    betweenEach = '',
    afterEach = '',
    after = '',
  } = {},
): string => {
  let html = before;

  map(contacts, (contact: Contact, index: number) => {
    html += beforeEach;
    html += index > 0 ? betweenEach : '';
    html += `
      <p class="${classContactAgency}">
        ${contact.name ? `<i class="fas fa-user pcolor"></i>${contact.name}${contact.title ? ` - ${contact.title}` : ''}<br>` : ''}
        ${contact.email ? `<i class="fas fa-envelope pcolor"></i><a href="mailto:${contact.email}">${contact.email}</a><br>` : ''}
        ${contact.singleOfficePhone ? `<i class="fas fa-phone pcolor"></i><a href="tel:${contact.singleOfficePhone}">${contact.singleOfficePhone}</a><br>` : ''}
      </p>
    `;
    html += afterEach;
  });

  return html + after;
};

export const generateAgencies = (
  agencies: Agency[],
  {
    before = '',
    beforeEach = '',
    betweenEach = '',
    afterEach = '',
    after = '',
    contactsOptions = {},
  } = {},
): string => {
  let html = before;

  map(agencies, (agency: Agency, index: number) => {
    const contacts = agency?.contacts || [];
    const contactsHTML = generateContacts(contacts, contactsOptions);

    if (agency.label || agency.address || (size(contacts) && !isEmpty(contacts[0]))) {
      html += beforeEach;
      html += index > 0 ? betweenEach : '';
      html += `
      <p class="center ${classAgency}" style="clear: both;line-height: 1.1rem; margin: auto auto 5px;">
        ${agency.label ? `<b class="cursor">${agency.label}</b><br>` : ''}
        ${agency.address ? `<small>${agency.address}</small><br><hr class="agency-address-separator" />` : ''}
        ${contactsHTML}
      </p>
    `;
      html += afterEach;
    }
  });

  // If there isn't any agency to display, then bypass before/after content
  if (html === before) {
    return null;
  }

  return html + after;
};

/**
 * Generate HTML code which displays the contacts of a student solution
 * If the partner has more than one agency or more than one contact in its agency, then we generate a "display more" link.
 *
 * @param studentSolution
 * @returns {string}
 */
export const generateStudentSolutionAgenciesContactHTML = (studentSolution: StudentSolution): string => {
  const offices: Office[] = groupContactsByOffice(studentSolution?.contactsBasicMatches);

  // Handle more complex UI when there are several agencies or several contacts
  if (size(offices) > 1 || size(offices?.[0]?.contacts) > 1) {
    // More than one agency or more than one contact, generate complex HTML (show/hide)
    const id = `${studentSolution?.ref}-${uuid()}-container`;
    const agenciesHTML = generateAgencies(offices);

    return `
      <div id="display-${id}" style="cursor: pointer;"
           onclick="document.querySelector('#${id}').style.display = 'block'; document.querySelector('#display-${id}').style.display = 'none';">
        <b>Afficher les détails</b>
      </div>
      <div id="${id}" style="display: none; max-height: 300px; overflow: auto;">
        <p style="float: right; cursor: pointer; margin-bottom: 0;">
          <i
            onclick="document.querySelector('#${id}').style.display = 'none';document.querySelector('#display-${id}').style.display = 'block';"
            class="fas fa-times-circle pcolor" style="margin-right: 15px !important;"
          ></i>
        </p>
        ${agenciesHTML}
      </div>
    `;
  } else {
    // Simple use-case, 1 contact only
    return generateAgencies(offices);
  }
};

export const generateStudentSolutionBenefitsHTML = (studentSolution: StudentSolution): string => {
  if (hasValue(studentSolution, 'benefitsDescription')) {
    const benefitsDescription = renderToString(<Markdown text={studentSolution.benefitsDescription} />);

    return `
      <div class="benefits">
        ${benefitsDescription}
      </div>
    `;
  } else {
    return '';
  }
};

export const generateStudentSolutionCriteriaHTML = (studentSolution: StudentSolution): string => {
  if (hasValue(studentSolution, 'eligibilityConditionsDescription')) {
    const eligibilityConditionsDescription = renderToString(<Markdown text={studentSolution.eligibilityConditionsDescription} />);

    return `
      <div class="eligibility-conditions-description">
        ${eligibilityConditionsDescription}
      </div>
    `;
  } else {
    return '';
  }
};

export const generateStudentSolutionContactHTML = (studentSolution: StudentSolution): string => {
  let html = '';
  html += generateStudentSolutionAgenciesContactHTML(studentSolution) || '';
  html += generateStudentSolutionBenefitsHTML(studentSolution);
  html += generateStudentSolutionCriteriaHTML(studentSolution);

  return html;
};

export const generateLogoHTML = (logo: Logo): string => {
  let width = logo?.width;
  let height = logo?.height;

  if (!width || !height) {
    // Apply default transformations if they weren't customised, won't be pretty, shouldn't happen
    width = 100;
    height = 90;
  }

  // Divide dimensions by 2 on mobile devices
  if (isMobileDevice()) {
    width /= 2;
    height /= 2;
  }

  return `
    <a
      href="${hasValue(logo, 'linkUrl') ? logo?.linkUrl : ''}"
      title="${logo?.title}"
      target="${hasValue(logo, 'linkTarget') ? get(logo, 'linkTarget', '_blank') : '_blank'}"
    >
      <img
        src="${logo?.url}"
        alt="${logo?.title}"
        width="${width}px"
        height="${height}px"
      />
    </a>
  `;
};

export const generateAdditionalLinksHTML = (t: TFunction, links: LabeledLink[]): string => {
  let linksHTML = '';

  map(links, (link: LabeledLink): void => {
    const { url, label } = link;

    linksHTML += `
      <p>
        <a
          href="${url}"
          target="${'_blank'}"
        >
          ${label || t('simulatorPage.SLSSettings.partners.link.defaultLabel', `En savoir plus`)}
        </a>
      </p>
    `;
  });

  return linksHTML;
};

export const generateAdditionalDocumentsHTML = (t: TFunction, documents: Asset[]): string => {
  let documentsHTML = '';

  map(documents, (document: Asset): void => {
    documentsHTML += `
      <p>
        <a
          href="${document.url}"
          target="_blank"
        >
          ${document.title || document.filename || t('simulatorPage.SLSSettings.partners.document.defaultLabel', `Lien`)}
        </a>
      </p>
    `;
  });

  return documentsHTML;
};

/**
 * Generates the description as HTML.
 *
 * History:
 *  Was first written using standard JS (not React), then imported as-it without much changes.
 *  Then, some part were updated to React in order to support Markdown.
 *  But, the whole thing should be re-written from scratch really.
 *  It must return a string though, but it could use actual components that are being rendered to string at the end, and the string would then be passed down to SLS.
 *
 * @param t
 * @param studentSolution
 */
export const generateStudentSolutionDescriptionHTML = (t: TFunction, studentSolution: StudentSolution): string => {
  const description = renderToString(<Markdown text={studentSolution.description} />);

  return `
    <div>
      ${hasValue(studentSolution, 'description') ? description : ''}
      ${hasValue(studentSolution, 'loanInterestRate') ? `<p>${t(`simulatorPage.SLSSettings.partners.loanInterestRate.label`, `Taux d'intérêts de <b>{{ loanInterestRate }}%</b>`, { loanInterestRate: studentSolution?.loanInterestRate })}</p>` : ''}
      ${hasValue(studentSolution, 'links') ? generateAdditionalLinksHTML(t, studentSolution?.links) : ''}
      ${hasValue(studentSolution, 'documents') ? generateAdditionalDocumentsHTML(t, studentSolution?.documents) : ''}
    </div>
  `;
};
